import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";

type BillingSummaryResponse = {
  default_payment_method: string | null;
  payment_methods: Array<{
    id: string;
    brand: string | null | undefined;
    last4: string | null | undefined;
    exp: string | null;
  }>;
  subscription: {
    id: string;
    status: string;
    price: string | null | undefined;
  } | null;
};

type SetupIntentResponse = { clientSecret?: string | null };

type DefaultPMResponse = {
  ok: boolean;
  default_payment_method: string | null | undefined;
};

type ChangePlanResponse = {
  id: string;
  status: string;
  price: string | null | undefined;
};

type RetryPaymentResponse = {
  ok: boolean;
  has_more?: boolean;
};

export async function fetchBillingSummary(customerId: string): Promise<BillingSummaryResponse> {
  const callable = httpsCallable<{ customerId: string }, BillingSummaryResponse>(functions, "getBillingSummary");
  const { data } = await callable({ customerId });
  return data;
}

export async function createPMSetupIntent(customerId: string): Promise<string> {
  const callable = httpsCallable<{ customerId: string }, SetupIntentResponse>(functions, "createSetupIntent");
  const { data } = await callable({ customerId });
  if (!data?.clientSecret) {
    throw new Error("Missing SetupIntent client secret");
  }
  return data.clientSecret;
}

export async function setDefaultPM(customerId: string, paymentMethodId: string): Promise<DefaultPMResponse> {
  const callable = httpsCallable<{ customerId: string; paymentMethodId: string }, DefaultPMResponse>(
    functions,
    "setDefaultPaymentMethod"
  );
  const { data } = await callable({ customerId, paymentMethodId });
  return data;
}

export async function changePlan(subscriptionId: string, newPriceId: string, promoCode?: string): Promise<ChangePlanResponse> {
  const callable = httpsCallable<{ subscriptionId: string; newPriceId: string; promo_code?: string }, ChangePlanResponse>(
    functions,
    "changeSubscriptionPrice"
  );
  const payload: { subscriptionId: string; newPriceId: string; promo_code?: string } = {
    subscriptionId,
    newPriceId,
  };
  if (promoCode && promoCode.trim()) {
    payload.promo_code = promoCode.trim();
  }
  const { data } = await callable(payload);
  return data;
}

export async function cancelSubscription(subscriptionId: string): Promise<{ ok: boolean; subscription: { id: string; status: string } }> {
  const callable = httpsCallable<{ subscriptionId: string }, { ok: boolean; subscription: { id: string; status: string } }>(
    functions,
    "cancelSubscription"
  );
  const { data } = await callable({ subscriptionId });
  return data;
}

export async function retrySubscriptionPayment(subscriptionId: string): Promise<RetryPaymentResponse> {
  const callable = httpsCallable<{ subscriptionId: string }, RetryPaymentResponse>(functions, "stripeCheckActive");
  const { data } = await callable({ subscriptionId });
  return data;
}
